import { MapPin, Building, Globe, Clock, ExternalLink } from 'lucide-react';
import { InfoSection, InfoField } from '@/core/components/ui/detail-blade';
import { KioskSessionGridRow, KioskSessionDetail } from '../types/kiosk-session-types';

interface LocationInfoSectionProps {
  session: KioskSessionGridRow | null;
  detail: KioskSessionDetail | undefined;
}

export function LocationInfoSection({ session, detail }: LocationInfoSectionProps) {
  if (!detail) {
    return null;
  }

  const formatAddress = () => {
    const parts = [detail.address, detail.city, detail.postal_code, detail.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const hasCoordinates = detail.latitude && detail.longitude;
  const googleMapsUrl = hasCoordinates
    ? `https://www.google.com/maps?q=${detail.latitude},${detail.longitude}`
    : null;

  return (
    <InfoSection
      title="Location & Device"
      icon={MapPin}
      collapsible={true}
      defaultExpanded={true}
      storageKey="kioskSessionDetail.locationInfo.expanded"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <InfoField
            label="Location Name"
            value={detail.location_name}
            icon={Building}
          />

          <InfoField
            label="Location ID"
            value={detail.location_id}
            type="id"
            copyable
          />

          {formatAddress() && (
            <InfoField
              label="Address"
              value={formatAddress()!}
              icon={MapPin}
            />
          )}

          {detail.timezone && (
            <InfoField
              label="Timezone"
              value={detail.timezone}
              icon={Clock}
            />
          )}
        </div>

        <div className="space-y-3">
          {detail.country_code && detail.party_id && (
            <InfoField
              label="OCPI Identifier"
              value={`${detail.country_code}-${detail.party_id}`}
              icon={Globe}
              copyable
            />
          )}

          {detail.machine_id != null && detail.machine_id > 0 && (
            <InfoField
              label="Machine ID"
              value={detail.machine_id.toString()}
              type="id"
              copyable
            />
          )}

          {detail.site_id != null && detail.site_id > 0 && (
            <InfoField
              label="Site ID"
              value={detail.site_id.toString()}
              type="id"
            />
          )}

          {googleMapsUrl && (
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-muted-foreground">Location</span>
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-primary hover:underline"
              >
                View on Map
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      </div>
    </InfoSection>
  );
}